import {
  ASSESSMENT_TYPE,
  BAGGAGE_TYPE,
} from '@web-platform/shared-universal-constants/src/fareAttributes';
import { TRIP_TYPES } from '@web-platform/shared-universal-constants/src/tripTypes';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { formatMoney } from '@web-platform/shared-universal-utils-frontend/src/helpers/formatMoney';
import { getPerPersonPrice } from '@web-platform/shared-universal-utils-frontend/src/helpers/getPerPersonPrice';
import {
  formatCurrency,
  formatTime,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { differenceInDays } from 'date-fns/differenceInDays';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';

import { translate } from '../translate';

import { parseFlightDurationToString } from './parseFlightDurationToString';

import type {
  AssessmentType,
  BaggageType,
} from '@web-platform/shared-universal-constants/src/fareAttributes';
import type { Money } from '@web-platform/shared-universal-constants/src/money';
import type { LegInfo } from '@web-platform/shared-universal-types/src/components/legDetails';
import type { FareAttributes } from '@web-platform/shared-universal-types/src/redux/state/entities/flights/pricingOption';

const SEPARATOR = ', ';

export const buildFlightsTicketPriceScreenReaderText = (
  totalPrice: number | null | undefined,
  numberOfPassengers: number = 1,
) => {
  if (!totalPrice) return translate('ktxtDayView_flightsTicket_ariaLabel_noPrice');

  if (numberOfPassengers > 1) {
    const perPassengerPrice = getPerPersonPrice(totalPrice, numberOfPassengers);
    return translate('ktxtDayView_flightsTicket_ariaLabel_costPerPassengerAndTotal', {
      formattedPricePerPassenger: formatCurrency(perPassengerPrice),
      formattedTotalPrice: formatCurrency(totalPrice),
    });
  }

  return translate('ktxtDayView_flightsTicket_ariaLabel_totalCostOnePassenger', {
    formattedTotalPrice: formatCurrency(totalPrice),
  });
};
export const getLegTimingSummary = (legInfo: LegInfo) => {
  const journeyTime = parseFlightDurationToString(legInfo, true);
  const { stopsInfo } = legInfo;
  if (!stopsInfo || !stopsInfo.places || stopsInfo.places.length === 0)
    return translate('ktxtDayView_flightsTicket_ariaLabel_directFlight', {
      journeyTime,
    });
  if (stopsInfo.places.length === 1)
    return translate('ktxtDayView_flightsTicket_ariaLabel_indirectFlightOneStop', {
      journeyTime,
      stopName: stopsInfo.places[0].name,
    });
  if (stopsInfo.places.length > 1)
    return translate('ktxtDayView_flightsTicket_ariaLabel_indirectFlightMultipleStops', {
      journeyTime,
      numberOfStops: String(stopsInfo.count),
      commaSeparatedStopNames: stopsInfo.places.map((place) => place.name).join(SEPARATOR),
    });
  return '';
};

type TimetableTimes = {
  departureTime: string;
  arrivalTime: string;
};

const getTimetableTranslation = (legInfo: LegInfo & TimetableTimes) =>
  translate('ktxtDayView_flightsTicket_ariaLabel_flightTimetableInfo', {
    departureTime: legInfo.departureTime,
    originName: legInfo.originName,
    arrivalTime: legInfo.arrivalTime,
    destinationName: legInfo.destinationName,
  });

const getAirportChangeWarning = ({ stopsInfo }: LegInfo) =>
  stopsInfo &&
  stopsInfo.places
    .map((place) =>
      place.changeAirport
        ? ` ${translate('ktxtDayView_flightsTicket_ariaLabel_changeAirport', {
            changeAirportLocation: place.name,
          })}`
        : '',
    )
    .join(' ');

const getTimeDescription = (legInfo: LegInfo) => {
  const daysBetween = differenceInDays(
    startOfDay(parseISO(legInfo.arrival)),
    startOfDay(parseISO(legInfo.departure)),
  );
  const departureTime = formatTime(parseISO(legInfo.departure), 'short');
  const arrivalTime = formatTime(parseISO(legInfo.arrival), 'short');

  if (daysBetween > 1) {
    return {
      departureTime,
      arrivalTime: translate('flightsTicket_ariaLabel_arrivalNDaysLater', {
        date: arrivalTime,
        days: String(daysBetween),
      }),
    };
  }

  if (daysBetween === 1) {
    return {
      departureTime,
      arrivalTime: translate('flightsTicket_ariaLabel_arrivalNextDay', {
        date: arrivalTime,
      }),
    };
  }

  return {
    departureTime,
    arrivalTime,
  };
};

export const buildFlightsTicketBaggageScreenReaderText = (fareAttributes: FareAttributes): string =>
  [BAGGAGE_TYPE.CABIN_BAGGAGE, BAGGAGE_TYPE.CHECKED_BAGGAGE]
    .map((baggageType) =>
      getBaggageAriaText(fareAttributes[baggageType]?.assessment, baggageType, {
        pieces: fareAttributes[baggageType]?.pieces ?? 0,
        showPaidBag: true,
      }),
    )
    .filter((item) => !!item)
    .join(' ');

export const getBaggageAriaText = (
  assessment: AssessmentType = ASSESSMENT_TYPE.UNKNOWN,
  baggageType: BaggageType,
  additionalInfo: {
    fee?: Money;
    pieces?: number;
    showPaidBag?: boolean;
    showFee?: boolean;
  },
) => {
  const enableInclusionIcons = getFeatureDecision(
    FEATURE_DECISION_KEYS.ENABLE_BAGGAGE_INCLUSION_ICONS,
  );
  if (enableInclusionIcons) {
    if (assessment === ASSESSMENT_TYPE.INCLUDED) {
      return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
        ? translate('a11y_baggage_badge_cabin_bag_included')
        : translate('a11y_baggage_badge_checked_bag_included');
    }
    if (assessment === ASSESSMENT_TYPE.UNAVAILABLE) {
      return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
        ? translate('a11y_baggage_badge_cabin_bag_unavailable')
        : translate('a11y_baggage_badge_checked_bag_unavailable');
    }
    if (assessment === ASSESSMENT_TYPE.UNKNOWN) {
      return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
        ? translate('a11y_baggage_badge_cabin_bag_unknown')
        : translate('a11y_baggage_badge_checked_bag_unknown');
    }
  }

  if (assessment === ASSESSMENT_TYPE.INCLUDED) {
    if (additionalInfo?.pieces === 1) {
      return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
        ? translate('cabin_bag_included_quantity_singular', { quantity: additionalInfo.pieces })
        : translate('checked_bag_included_quantity_singular', { quantity: additionalInfo.pieces });
    }
    return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
      ? translate('cabin_bag_included_quantity_plural', { quantity: additionalInfo.pieces })
      : translate('checked_bag_included_quantity_plural', { quantity: additionalInfo.pieces });
  }
  if (assessment === ASSESSMENT_TYPE.UNAVAILABLE) {
    return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
      ? translate('cabin_bag_not_included')
      : translate('checked_bag_not_included');
  }
  if (assessment === ASSESSMENT_TYPE.FEE && additionalInfo.showPaidBag) {
    return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
      ? translate('cabin_bag_not_included')
      : translate('checked_bag_not_included');
  }
  if (assessment === ASSESSMENT_TYPE.FEE && additionalInfo.showFee) {
    if (additionalInfo.fee?.amount === '0') {
      return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
        ? translate('cabin_bag_available_fee_unknown')
        : translate('checked_bag_available_fee_unknown');
    }

    return baggageType === BAGGAGE_TYPE.CABIN_BAGGAGE
      ? translate('cabin_bag_available_fee_known', { price: formatMoney(additionalInfo.fee) })
      : translate('checked_bag_available_fee_known', { price: formatMoney(additionalInfo.fee) });
  }
  return undefined;
};

export const getTripType = (numberOfLegs: number) => {
  if (numberOfLegs === 1) {
    return TRIP_TYPES.ONE_WAY;
  }

  if (numberOfLegs === 2) {
    return TRIP_TYPES.RETURN;
  }

  if (numberOfLegs > 2) {
    return TRIP_TYPES.MULTI_DESTINATION;
  }

  return '';
};

type AriaLabelStrings = {
  id: string;
  operatorInfo: string;
  timetableInfo: string;
  legTimingSummary: string;
  warnings: string | null | undefined;
};

export const buildFlightsTicketScreenReaderText = (legsInfo: LegInfo[]): AriaLabelStrings[] => {
  const tripType = getTripType(legsInfo.length);

  switch (tripType) {
    case TRIP_TYPES.ONE_WAY: {
      const [legInfo] = legsInfo;
      const oneWayOperatorsInfo = translate('ktxtDayView_flightsTicket_ariaLabel_flightWith', {
        operator: legInfo.legOperators.join(SEPARATOR),
      });

      const timetableTimes = getTimeDescription(legInfo);
      const oneWayTimetable = getTimetableTranslation({ ...legInfo, ...timetableTimes });
      const legTimingSummary = getLegTimingSummary(legInfo);
      const airportChangeWarning = getAirportChangeWarning(legInfo);
      return [
        {
          id: legInfo.id,
          operatorInfo: oneWayOperatorsInfo,
          timetableInfo: oneWayTimetable,
          legTimingSummary,
          warnings: airportChangeWarning,
        },
      ];
    }

    case TRIP_TYPES.RETURN: {
      const [outbound, inbound] = legsInfo;
      const outboundOperatorsInfo = translate(
        'ktxtDayView_flightsTicket_ariaLabel_outboundFlight',
        {
          operator: outbound.legOperators.join(SEPARATOR),
        },
      );

      const inboundOperatorsInfo = translate('ktxtDayView_flightsTicket_ariaLabel_inboundFlight', {
        operator: inbound.legOperators.join(SEPARATOR),
      });

      const outboundTimetableTimes = getTimeDescription(outbound);
      const outboundTimetable = getTimetableTranslation({ ...outbound, ...outboundTimetableTimes });
      const inboundTimetableTimes = getTimeDescription(inbound);
      const returnTimetable = getTimetableTranslation({ ...inbound, ...inboundTimetableTimes });
      const outboundLegTimingSummary = getLegTimingSummary(outbound);
      const outboundAirportChangeWarning = getAirportChangeWarning(outbound);
      const inboundLegTimingSummary = getLegTimingSummary(inbound);
      const inboundAirportChangeWarning = getAirportChangeWarning(inbound);
      return [
        {
          id: outbound.id,
          operatorInfo: outboundOperatorsInfo,
          timetableInfo: outboundTimetable,
          legTimingSummary: outboundLegTimingSummary,
          warnings: outboundAirportChangeWarning,
        },
        {
          id: inbound.id,
          operatorInfo: inboundOperatorsInfo,
          timetableInfo: returnTimetable,
          legTimingSummary: inboundLegTimingSummary,
          warnings: inboundAirportChangeWarning,
        },
      ];
    }

    case TRIP_TYPES.MULTI_DESTINATION:
    default: {
      return legsInfo.map((legInfo, legIndex) => {
        const operatorInfo = translate('ktxtDayView_flightsTicket_ariaLabel_flightWithMultiCity', {
          operator: legInfo.legOperators.join(SEPARATOR),
          legNumber: String(legIndex + 1),
        });

        const timetableTimes = getTimeDescription(legInfo);
        const timetableInfo = getTimetableTranslation({ ...legInfo, ...timetableTimes });
        const legTimingSummary = getLegTimingSummary(legInfo);
        const airportChangeWarning = getAirportChangeWarning(legInfo);
        return {
          id: legInfo.id,
          operatorInfo,
          timetableInfo,
          legTimingSummary,
          warnings: airportChangeWarning,
        };
      });
    }
  }
};
